import { Button, Divider, Flex, Spinner } from "@chakra-ui/react";
import { DropDown, Modal, SlotTemplateButton, Typography } from "@reconvert/react-ui-component";
import { useTranslation } from "react-i18next";
import { Box } from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo } from "react";
import BannerIcon from "./icons/BannerIcon";
import StartFromScratch from "./icons/StartFromScratch";
import BundleIcon from "./icons/BundleIcon";
import DuplicateSlotIcon from "./icons/DuplicateSlotIcon";
import ProductRecommendationSlotIcon from "./icons/ProductRecommendationSlot";
import ProductUpsellIcon from "./icons/ProductUpsellIcon";
import { useAppSelector } from "../../app/hooks";
import {
  MAX_CUSTOM_SLOT,
  PageType,
  PreMadeTemplateSlotMapping,
  PreMadeTemplateType,
  SlotLocations,
} from "@reconvert/reconvert-utils";
import useSlots from "../../hooks/useSlots";
import { selectSlotsState } from "../../app/slice/slotSlice";
import { getVariantsList } from "../../services/conditions.service";
import { getPageLocationText } from "../../utils/getConditionText";
import Announcementbar from "./icons/Announcementbar";
import useIsActiveOnPage, { PagePurpose } from "../../hooks/useIsActiveOnPage";
import { UNLEASH_ENABLE_ANNOUNCEMENT_BAR, UNLEASH_ENABLE_QUANTITY_DISCOUNT } from "../../utils/constant";
import { useFlag } from "@unleash/proxy-client-react";
import { useDirection } from "../../hooks/useDirection";
import styled from "@emotion/styled";

const MIN_HEIGHT = 171;

const StyledDropDownWrapper = styled(Box)`
  & [role="listbox"] {
    max-width: 100%;
  }

  @media (max-width: 480px) {
    min-width: 100%;
    color: #6a7381;
    font-size: 16px;
    height: 48px;
  & [role="option"] {
    white-space: normal !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    word-break: break-word !important;
    max-width: 100%;
  }

  & .chakra-select__option {
    white-space: normal !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    word-break: break-word !important;
  }
`;

const mobileGridSx = {
  "& > div": {
    minWidth: "0 !important",
    maxWidth: "100% !important",
    overflow: "hidden",
  },
  "& svg": {
    maxWidth: "100%",
    height: "auto",
  },
};

export const getPreMadeTemplates = (
  slotType: PreMadeTemplateType | null,
  isAnnouncementBarAccessible: boolean = false,
) => {
  const templates = [
    {
      icon: <StartFromScratch />,
      hoverIcon: <StartFromScratch hover />,
      title: "Start from scratch",
      type: PreMadeTemplateType.CUSTOM_SLOT,
      category: "custom",
    },
    {
      icon: <DuplicateSlotIcon />,
      hoverIcon: <DuplicateSlotIcon hover />,
      title: "Duplicate existing slot",
      type: PreMadeTemplateType.DUPLICATE_SLOT,
      category: "custom",
    },
    {
      icon: <BundleIcon />,
      hoverIcon: <BundleIcon hover />,
      title: "Quantity Discount",
      type: PreMadeTemplateType.QUANTITY_DISCOUNT,
      allowPages: [PageType.PRE_PURCHASE_PRODUCT_PAGE],
      slotNo: PreMadeTemplateSlotMapping[PreMadeTemplateType.QUANTITY_DISCOUNT]?.slotNo,
      category: "premade",
    },
    {
      icon: <ProductUpsellIcon />,
      hoverIcon: <ProductUpsellIcon hover />,
      title: "Product upsell",
      type: PreMadeTemplateType.PRODUCT_UPSELL,
      slotNo: PreMadeTemplateSlotMapping[PreMadeTemplateType.PRODUCT_UPSELL]?.slotNo,
      category: "premade",
    },
    {
      icon: <ProductRecommendationSlotIcon />,
      hoverIcon: <ProductRecommendationSlotIcon hover />,
      title: "Product recommendations",
      type: PreMadeTemplateType.PRODUCT_RECOMMENDATION,
      slotNo: PreMadeTemplateSlotMapping[PreMadeTemplateType.PRODUCT_RECOMMENDATION]?.slotNo,
      category: "premade",
    },
    {
      icon: <BannerIcon />,
      hoverIcon: <BannerIcon hover />,
      title: "Banner",
      type: PreMadeTemplateType.BANNER,
      allowPages: SlotLocations,
      slotNo: PreMadeTemplateSlotMapping[PreMadeTemplateType.BANNER]?.slotNo,
      category: "premade",
    },
    {
      icon: <BundleIcon />,
      hoverIcon: <BundleIcon hover />,
      title: "Bundle",
      type: PreMadeTemplateType.BUNDLE,
      slotNo: PreMadeTemplateSlotMapping[PreMadeTemplateType.BUNDLE]?.slotNo,
      category: "premade",
    },
  ];

  if (isAnnouncementBarAccessible && (slotType === PreMadeTemplateType.ANNOUNCEMENT_BAR || slotType === null)) {
    templates.push({
      icon: <Announcementbar />,
      hoverIcon: <Announcementbar hover />,
      title: "Announcement bar",
      type: PreMadeTemplateType.ANNOUNCEMENT_BAR,
      slotNo: PreMadeTemplateSlotMapping[PreMadeTemplateType.ANNOUNCEMENT_BAR]?.slotNo,
      category: "premade",
    });
  }

  return templates;
};

const PreMadeTemplateModal = ({
  showPreMadeTemplateModal,
  slotNo,
  onClose,
  slotType,
}: {
  showPreMadeTemplateModal: boolean;
  slotNo: number | null;
  onClose: () => void;
  slotType: PreMadeTemplateType | null;
}) => {
  const { t } = useTranslation();
  const [selectedTemplateType, setSelectedTemplateType] = React.useState<PreMadeTemplateType | null>(null);
  const { slots } = useAppSelector(state => state.slots);
  const { activePage } = useAppSelector(state => state.app);
  const { creatingSlot, isButtonDisabled } = useAppSelector(selectSlotsState);
  const { handleCreateSlot } = useSlots();
  const [loading, setLoading] = React.useState(true);
  const [variants, setVariants] = React.useState([]);
  const [variantId, setVariantId] = React.useState("");

  const isAnnouncementBarEnabled = useFlag(UNLEASH_ENABLE_ANNOUNCEMENT_BAR);
  const isQuantityDiscountEnabled = useFlag(UNLEASH_ENABLE_QUANTITY_DISCOUNT);

  const isAnnouncementBarAccessible = useIsActiveOnPage(
    PagePurpose.ANNOUNCEMENT_BAR_ACCESSIBLE,
    activePage!,
    isAnnouncementBarEnabled,
  );

  const isAllCustomSlotsCreated =
    new Set<number>(slots?.filter(a => a.slotNo <= MAX_CUSTOM_SLOT).map(a => a.slotNo))?.size === MAX_CUSTOM_SLOT;

  const fetchVariants = useCallback(async () => {
    try {
      const res = await getVariantsList(activePage!);

      if (res.length > 0) {
        setVariants(
          res.map((variant: any) => {
            return {
              label: `${t(getPageLocationText(variant.location as PageType))} | ${t(variant?.conditionName)} | ${t(variant?.slotName)} | ${t(variant?.name)}`,
              value: variant.id,
            };
          }),
        );
      }
    } catch (error) {
      console.error("Error while fetching variants", error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage]);

  useEffect(() => {
    if (selectedTemplateType === PreMadeTemplateType?.DUPLICATE_SLOT && variants.length === 0) {
      fetchVariants();
    }
  }, [fetchVariants, selectedTemplateType]);

  /**
   * Filtering logic:
   * - If all custom slots have been created, remove templates of type `CUSTOM_SLOT` and `DUPLICATE_SLOT`.
   * - If a template's slot is already present in the `slots` array, remove that template.
   */
  const filteredPreMadeTemplates = useMemo(() => {
    // Filter templates based on whether they allow the active page
    const filteredTemplates = getPreMadeTemplates(slotType, isAnnouncementBarAccessible).filter(
      a =>
        (!a.allowPages || a.allowPages.includes(activePage!)) &&
        (a.type !== PreMadeTemplateType.QUANTITY_DISCOUNT || isQuantityDiscountEnabled),
    );

    if (slotNo) {
      return filteredTemplates;
    }

    // Filter templates based on whether all custom slots have been created and whether there are available slots for the template
    return filteredTemplates.filter(template => {
      if (
        isAllCustomSlotsCreated &&
        (template.type === PreMadeTemplateType.CUSTOM_SLOT || template.type === PreMadeTemplateType.DUPLICATE_SLOT)
      )
        return false;

      const templateSlotNo = PreMadeTemplateSlotMapping[template.type]?.slotNo;
      const isSlotAvailable = !slots.some(slot => slot.slotNo === templateSlotNo);

      return isSlotAvailable;
    });
  }, [slotNo, activePage, isAllCustomSlotsCreated, slots, isAnnouncementBarAccessible, isQuantityDiscountEnabled]);

  const hasMoreThanOneCustomTemplate = useMemo(
    () => filteredPreMadeTemplates.filter(t => t.category === "custom").length > 0,
    [filteredPreMadeTemplates],
  );

  const hasMoreThanOnePremadeTemplate = useMemo(
    () => filteredPreMadeTemplates.filter(t => t.category === "premade").length > 0,
    [filteredPreMadeTemplates],
  );

  const buttonDisabled = useMemo(() => {
    if (selectedTemplateType === PreMadeTemplateType?.DUPLICATE_SLOT) {
      return variants.length === 0 || !variantId || isButtonDisabled;
    } else {
      return !selectedTemplateType || isButtonDisabled;
    }
  }, [selectedTemplateType, variants.length, variantId, isButtonDisabled]);

  const createSlotWithTemplate = (templateType: PreMadeTemplateType) => {
    if (templateType === PreMadeTemplateType.DUPLICATE_SLOT) {
      handleCreateSlot(slotNo, null, variantId);
      return;
    }

    if (templateType === PreMadeTemplateType.CUSTOM_SLOT) {
      handleCreateSlot && handleCreateSlot(slotNo, null, null);
      return;
    }

    const templateSlotNo = PreMadeTemplateSlotMapping[templateType]?.slotNo;

    handleCreateSlot && handleCreateSlot(slotNo, templateSlotNo || null, null);
  };

  return (
    <PreMadeTemplateModalWrapper isOpen={showPreMadeTemplateModal} onClose={() => {
      onClose();
      setVariants([]);
      setSelectedTemplateType(null);
    }} title={!!slotNo ? t("Add widgets") : t("Create a slot")}>
      {!hasMoreThanOneCustomTemplate && !hasMoreThanOnePremadeTemplate ? (
        <>
          <Flex height={"158px"} flex={1} justifyContent={"center"} alignItems={"center"}>
            <Spinner size={"lg"} />
          </Flex>
        </>
      ) : (
        <>
          {hasMoreThanOnePremadeTemplate && hasMoreThanOneCustomTemplate && (
            <Flex justifyContent={"center"} alignItems={"center"} mt={5}>
              <Typography variant={"body"} fontWeight={500}>
                {!!slotNo ? t("Templates") : t("Pre-made slot")}
              </Typography>
            </Flex>
          )}
          <Box
            sx={{ justifyContent: "center" }}
            display={"flex"}
            mt={3}
            flexWrap={"wrap"}
            justifyContent={"center"}
            rowGap={"15px"}
            padding={{ base: "0px 50px", md: "0px 50px" }}
            columnGap={{ base: "8px", md: "15px" }}
            mx={"auto"}
          >
            {filteredPreMadeTemplates.map(
              a =>
                a.category === "premade" && (
                  <SlotTemplateButton
                    hoverIcon={a.hoverIcon}
                    icon={a.icon}
                    title={t(a.title)}
                    isSelected={selectedTemplateType === a.type}
                    checkbox={false}
                    onClick={isSelected => {
                      if (isSelected) {
                        setSelectedTemplateType(a.type);
                      } else {
                        setSelectedTemplateType(null);
                      }
                    }}
                    minHeight={MIN_HEIGHT}
                  />
                ),
            )}
          </Box>
          {hasMoreThanOneCustomTemplate && hasMoreThanOnePremadeTemplate && (
            <Flex justifyContent={"center"} alignItems={"center"} mt={5}>
              <Typography variant={"body"} fontWeight={500}>
                {!!slotNo ? t("Custom") : t("Custom slot")}
              </Typography>
            </Flex>
          )}
          <Box
            sx={{ justifyContent: "center" }}
            display={"flex"}
            mt={3}
            flexWrap={"wrap"}
            justifyContent={"center"}
            gap={{ base: "8px", md: "24px" }}
            mx={"auto"}
          >
            {filteredPreMadeTemplates.map(
              a =>
                a.category === "custom" && (
                  <SlotTemplateButton
                    icon={a.icon}
                    hoverIcon={a.hoverIcon}
                    title={t(a.title)}
                    isSelected={selectedTemplateType === a.type}
                    checkbox={false}
                    onClick={isSelected => {
                      if (isSelected) {
                        setSelectedTemplateType(a.type);
                      } else {
                        setSelectedTemplateType(null);
                      }
                    }}
                    minHeight={MIN_HEIGHT}
                  />
                ),
            )}
          </Box>
          <Flex justifyContent={"center"} alignItems={"center"}>
            {selectedTemplateType === "DUPLICATE_SLOT" && (
              <Box display={"flex"} justifyContent={"center"} alignItems={"center"} mt="5">
                {loading ? (
                  <Box height={"36px"} flex={1}>
                    <Spinner size={"lg"} />
                  </Box>
                ) : (
                  <Flex
                    alignItems="center"
                    justifyContent="center"
                    gap={"10px"}
                    flexDirection={{ base: "column", md: "row" }}
                  >
                    <Typography variant="body">{t("Select Slot")}</Typography>
                    <StyledDropDownWrapper flex={1} width={{ base: "100%", md: "374px" }}>
                      <DropDown
                        placeholder={t("Select slot to duplicate")}
                        onValueChange={(val: string | string[]) => setVariantId(val as string)}
                        options={variants || []}
                        value={variantId}
                        name="variantSelector"
                        size="sm"
                        autowrap
                      />
                    </StyledDropDownWrapper>
                  </Flex>
                )}
              </Box>
            )}
          </Flex>
        </>
      )}
      <Box mx="-24px" mt={3}>
        <Divider color="divider.1" />
      </Box>

      <Box display="flex" justifyContent={"end"} mt={5} mb={3}>
        <Button
          colorScheme="black"
          variant="secondary"
          mr={2}
          onClick={() => {
            onClose();
            setVariants([]);
            setSelectedTemplateType(null);
          }}
        >
          {t("Cancel")}
        </Button>
        <Button
          padding={"8px 16px"}
          variant={"solid"}
          isDisabled={buttonDisabled}
          onClick={() => createSlotWithTemplate(selectedTemplateType!)}
          isLoading={creatingSlot}
        >
          {!!slotNo ? t("Add") : t("Create")}
        </Button>
      </Box>
    </PreMadeTemplateModalWrapper>
  );
};

const PreMadeTemplateModalWrapper = ({ children, isOpen, onClose, title }: { children: React.ReactNode, isOpen: boolean, onClose: () => void, title: string }) => {
  const { direction } = useDirection();

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
      }}
      closeOnEsc={true}
      title={title}
      size="4xl"
      hideFooter
      isDividerVisible={true}
      direction={direction}
    >
      {children}
    </Modal>
  );
};

export default PreMadeTemplateModal;
